/*
 * Court Shoe Finder - questions and scoring (assets/court-shoe-finder-engine.js)
 *
 * Used by the Tennis Racquet Finder (sport "tennis") and the Paddle Finder
 * Kiosk (sport "pickleball"). Each app draws its own screens; this file only
 * decides which shoes fit an answer set and explains why.
 *
 * Facts come from the "shoe" product metafields (see SHOE-FINDER.md), copied
 * from Tennis Warehouse's fit details: width fit, length fit, arch, weight,
 * break-in and durability guarantee. "Wide" in a product title or a Width
 * option is store data. Nothing is estimated: missing facts stay missing and
 * count as neutral. Sizes and stock come from the product's variants.
 *
 * Plain ES5 for older kiosk browsers. Loadable in Node for tests.
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.CourtShoeFinder = factory();
})(this, function () {
  "use strict";

  // `size` is answered on a grid of sizes (see sizesFor); the others are buttons.
  var QUESTIONS = [
    { id: "who", text: "Who are the shoes for?", options: [
      { id: "men", text: "Men's", sub: "Men's sizes" },
      { id: "women", text: "Women's", sub: "Women's sizes" },
      { id: "kids", text: "Kids", sub: "Junior sizes", kidsOnly: true }
    ] },
    { id: "size", text: "What size do you wear?", grid: true, sub: "We'll only show shoes we have in your size." },
    { id: "width", text: "How wide are your feet?", options: [
      { id: "narrow", text: "Narrow", sub: "Most shoes feel roomy on me" },
      { id: "regular", text: "Regular", sub: "Most shoes fit me fine" },
      { id: "wide", text: "Wide", sub: "Most shoes feel tight on the sides" },
      { id: "unsure", text: "Not sure", sub: "" }
    ] },
    { id: "arch", text: "What are your arches like?", options: [
      { id: "low", text: "Low or flat", sub: "Most of my foot touches the ground" },
      { id: "regular", text: "Regular", sub: "A normal curve" },
      { id: "high", text: "High", sub: "A big curve under my foot" },
      { id: "unsure", text: "Not sure", sub: "" }
    ] },
    { id: "priority", text: "What matters most to you?", options: [
      { id: "durable", text: "Lasts a long time", sub: "Tough soles for lots of play" },
      { id: "light", text: "Light and quick", sub: "Easy to move fast" },
      { id: "comfort", text: "Comfortable right away", sub: "Little or no break-in" },
      { id: "value", text: "Best value", sub: "A great shoe for the price" }
    ] },
    { id: "often", text: "How often do you play?", options: [
      { id: "1", text: "Once a week or less", sub: "" },
      { id: "2", text: "2\u20133 times a week", sub: "" },
      { id: "4", text: "4 or more times a week", sub: "" }
    ] },
    { id: "budget", text: "What's your budget?", options: [
      { id: "b100", text: "Under $100", max: 9999 },
      { id: "b150", text: "Up to $150", max: 15000 },
      { id: "b200", text: "Up to $200", max: 20000 },
      { id: "any", text: "No set budget", max: null }
    ] }
  ];
  var BY_ID = {};
  QUESTIONS.forEach(function (q) { BY_ID[q.id] = q; });

  // Questions for the answers so far. Kids get size and budget only.
  function questionsFor(answers, pool) {
    var kids = answers && answers.who === "kids";
    return QUESTIONS.filter(function (q) {
      if (kids) return q.id === "who" || q.id === "size" || q.id === "budget";
      return true;
    }).map(function (q) {
      if (q.id !== "who") return q;
      var hasKids = (pool || []).some(function (p) { return genderOf(p) === "kids"; });
      return { id: q.id, text: q.text, options: q.options.filter(function (o) { return !o.kidsOnly || hasKids; }) };
    });
  }

  function optionById(qid, id) {
    var q = BY_ID[qid];
    if (!q || !q.options) return null;
    for (var i = 0; i < q.options.length; i++) if (q.options[i].id === id) return q.options[i];
    return null;
  }

  // ---------------------------------------------------------------------
  // Product facts
  // ---------------------------------------------------------------------
  function lower(s) { return String(s || "").toLowerCase(); }
  function inCol(p, re) { return (p.collections || []).some(function (c) { return re.test(c); }); }

  function isPadel(p) { return /padel/.test(lower(p.title)) || inCol(p, /^padel/); }

  function genderOf(p) {
    var t = lower(p.title).replace(/\u2019/g, "'");
    if (/junior|\bjr\b|\bkids?\b/.test(t) || inCol(p, /junior/)) return "kids";
    if (/women|womens|women's|ladies/.test(t)) return "women";
    if (/\bmen\b|\bmens\b|men's|mens'/.test(t)) return "men";
    if (inCol(p, /^womens/)) return "women";
    return "men";
  }

  function isPickleballShoe(p) {
    return /pickleball|\bpkl\b/.test(lower(p.title)) || (p.tags || []).some(function (t) { return /pickleball/i.test(t) && !/center/i.test(t); });
  }

  // Which finder a shoe belongs to. Tennis: the store's tennis shoe collections.
  // Pickleball: every court shoe except padel (all-court tennis shoes work on pickleball courts).
  function eligible(p, sport) {
    if (isPadel(p)) return false;
    if (sport === "tennis") return inCol(p, /tennis-shoes$/);
    return true;
  }

  function sizeOptionIndex(p) {
    var opts = p.options || [];
    for (var i = 0; i < opts.length; i++) if (/size/i.test(opts[i])) return i;
    return -1;
  }
  // "9.5" -> 9.5; "8.0M/9.5W" -> 8 (the men's size); anything else -> null.
  function parseSize(v) {
    var m = String(v || "").match(/^\s*(\d{1,2}(?:\.\d)?)/);
    return m ? parseFloat(m[1]) : null;
  }
  function availableSizes(p) {
    var oi = sizeOptionIndex(p);
    var out = [];
    (p.variants || []).forEach(function (v) {
      if (!v.available) return;
      var s = oi === -1 ? null : parseSize(v.options && v.options[oi]);
      if (s !== null && out.indexOf(s) === -1) out.push(s);
    });
    return out.sort(function (a, b) { return a - b; });
  }
  // First in-stock variant in `size` (and the wide width when asked for).
  function variantForSize(p, size, preferWide) {
    var oi = sizeOptionIndex(p);
    var wi = widthOptionIndex(p);
    var best = null;
    (p.variants || []).forEach(function (v) {
      if (!v.available || oi === -1) return;
      if (parseSize(v.options[oi]) !== size) return;
      var wide = wi !== -1 && /wide|2e|\bd\b/i.test(v.options[wi]) && !/standard/i.test(v.options[wi]);
      if (!best || (preferWide && wide)) best = v;
    });
    return best;
  }
  function widthOptionIndex(p) {
    var opts = p.options || [];
    for (var i = 0; i < opts.length; i++) if (/width/i.test(opts[i])) return i;
    return -1;
  }

  // Sizes to offer on the size screen: in stock for that group in this finder.
  function sizesFor(pool, who) {
    var all = [];
    pool.forEach(function (p) {
      if (genderOf(p) !== who) return;
      availableSizes(p).forEach(function (s) { if (all.indexOf(s) === -1) all.push(s); });
    });
    return all.sort(function (a, b) { return a - b; });
  }

  // Width class from the fit details: "snug" < "medium" < "roomy" < "wide".
  function widthClass(p) {
    if (/\bwide\b|\(2e\)|\b2e\b/i.test(p.title || "")) return "wide";
    var w = lower(p.specs && p.specs.width_fit);
    if (!w) return widthOptionIndex(p) !== -1 ? "medium" : null;
    if (/wide|2e/.test(w) && !/than medium/.test(w)) return "wide";
    if (/wider than medium|generous/.test(w)) return "roomy";
    if (/narrow|snug/.test(w)) return "snug";
    if (/medium/.test(w)) return "medium";
    return null;
  }
  // Also counts as wide when a Width option offers a wide version.
  function hasWideVersion(p) {
    var wi = widthOptionIndex(p);
    if (wi === -1) return false;
    return (p.variants || []).some(function (v) { return v.available && /wide|2e/i.test(v.options[wi]); });
  }

  // Arch support from the fit details: 1 low ... 3 medium.
  function archLevel(p) {
    var a = lower(p.specs && p.specs.arch);
    if (!a) return null;
    if (/low medium/.test(a)) return 2;
    if (/slightly low/.test(a)) return 1.5;
    if (/low/.test(a)) return 1;
    if (/high/.test(a)) return 3.5;
    if (/medium/.test(a)) return 3;
    return null;
  }

  function weightOz(p) { var w = parseFloat(p.specs && p.specs.weight_oz); return isNaN(w) ? null : w; }
  function guarantee(p) {
    var g = lower(p.specs && p.specs.durability_guarantee);
    if (!g) return null;
    return /^(yes|true)/.test(g);
  }
  function breakIn(p) {
    var b = lower(p.specs && p.specs.break_in);
    if (!b) return null;
    if (/none|^no/.test(b)) return "none";
    if (/slight/.test(b)) return "slight";
    return "some";
  }
  // Short sizing tip from the length fit, or null when it fits true to size.
  function sizingTipKey(p) {
    var l = String((p.specs && p.specs.length_fit) || "").trim();
    if (!l || /^true( to size)?$/i.test(l)) return null;
    if (/half size down|long/i.test(l)) return "tip_long";
    if (/half size|short|snug/i.test(l)) return "tip_short";
    return null;
  }
  function sizingTip(p) { var k = sizingTipKey(p); return k ? REASONS_EN[k] : null; }

  // ---------------------------------------------------------------------
  // Scoring
  // ---------------------------------------------------------------------
  var WEIGHTS = { width: 25, priority: 32, arch: 10, often: 8, budget: 10, sport: 10, stock: 5 };

  function clamp(x, a, b) { return x < a ? a : (x > b ? b : x); }

  function widthFit(cls, want, wideVersion) {
    if (!want || want === "unsure") return cls ? 0.7 : null;
    if (want === "wide" && wideVersion) return 1;
    if (!cls) return null;
    var table = {
      narrow: { snug: 1, medium: 0.7, roomy: 0.3, wide: 0 },
      regular: { snug: 0.7, medium: 1, roomy: 0.8, wide: 0.3 },
      wide: { snug: 0, medium: 0.2, roomy: 0.75, wide: 1 }
    };
    return table[want][cls];
  }

  function archFit(level, want) {
    if (level === null) return null;
    if (!want || want === "unsure") return 0.7;
    if (want === "low") return clamp(1 - (level - 1) * 0.25, 0, 1);
    return clamp(1 - Math.abs(3 - level) * 0.25, 0, 1); // regular or high: more arch support fits better
  }

  function priorityFit(p, want, ctx) {
    if (want === "durable") { var g = guarantee(p); return g === null ? null : (g ? 1 : 0.3); }
    if (want === "light") { var w = weightOz(p); return w === null ? null : clamp((ctx.heavy - w) / Math.max(0.5, ctx.heavy - ctx.light), 0, 1); }
    if (want === "comfort") { var b = breakIn(p); return b === null ? null : (b === "none" ? 1 : (b === "slight" ? 0.6 : 0.2)); }
    if (want === "value") return clamp((ctx.maxPrice - p.price) / Math.max(1, ctx.maxPrice - ctx.minPrice), 0, 1);
    return null;
  }

  function oftenFit(p, often) {
    var g = guarantee(p);
    if (often === "4") return g === null ? null : (g ? 1 : 0.4);
    if (often === "2") return g === null ? null : (g ? 1 : 0.7);
    return 1;
  }

  // Guards: shoes never recommended for an answer set.
  function guard(p, a) {
    var cls = widthClass(p);
    if (a.width === "wide" && cls === "snug" && !hasWideVersion(p)) return "too snug for wide feet";
    if (a.width === "narrow" && cls === "wide") return "too wide for narrow feet";
    return null;
  }

  function recommend(answers, products, options) {
    options = options || {};
    var sport = options.sport || "pickleball";
    var max = options.maxRecommendations || 3;
    var who = answers.who || "men";
    var size = answers.size && answers.size !== "any" ? parseFloat(answers.size) : null;
    var budgetOpt = optionById("budget", answers.budget);
    var cap = budgetOpt && budgetOpt.max !== null && budgetOpt.max !== undefined ? budgetOpt.max : null;

    var pool = products.filter(function (p) { return eligible(p, sport) && genderOf(p) === who && p.available; });
    var weights = pool.map(weightOz).filter(function (w) { return w !== null; });
    var prices = pool.map(function (p) { return p.price; });
    var ctx = {
      light: weights.length ? Math.min.apply(null, weights) : 0, heavy: weights.length ? Math.max.apply(null, weights) : 1,
      minPrice: prices.length ? Math.min.apply(null, prices) : 0, maxPrice: prices.length ? Math.max.apply(null, prices) : 1
    };

    var scored = [], rejected = [];
    pool.forEach(function (p) {
      var sizes = availableSizes(p);
      if (size !== null && sizes.indexOf(size) === -1) { rejected.push({ product: p, reason: "not in stock in size " + size }); return; }
      if (!(p.price > 0)) { rejected.push({ product: p, reason: "price missing ($0)" }); return; }
      var why = who === "kids" ? null : guard(p, answers);
      if (why) { rejected.push({ product: p, reason: why }); return; }
      var parts = {};
      if (who !== "kids") {
        parts.width = widthFit(widthClass(p), answers.width, hasWideVersion(p));
        parts.arch = archFit(archLevel(p), answers.arch);
        parts.priority = priorityFit(p, answers.priority, ctx);
        parts.often = oftenFit(p, answers.often);
        parts.sport = sport === "pickleball" ? (isPickleballShoe(p) ? 1 : 0.8) : 1;
      }
      parts.budget = cap === null || p.price <= cap ? 1 : 0;
      parts.stock = Math.min(1, sizes.length / 8); // more sizes in stock = easier to swap for a different fit
      // Feet that are clearly narrow or wide make width the deciding factor.
      var wts = {};
      Object.keys(WEIGHTS).forEach(function (k) { wts[k] = WEIGHTS[k]; });
      if (answers.width === "wide" || answers.width === "narrow") wts.width = 45;
      // "Lasts a long time" already rewards durability; don't count it twice.
      if (answers.priority === "durable") wts.often = 0;
      var total = 0, known = 0, possible = 0;
      Object.keys(wts).forEach(function (k) {
        if (!(k in parts) || !wts[k]) return;
        possible += wts[k];
        if (parts[k] === null || parts[k] === undefined) total += 0.5 * wts[k];
        else { total += parts[k] * wts[k]; known += wts[k]; }
      });
      var confidence = possible ? known / possible : 0;
      var score = possible ? (total / possible) * 100 * (0.7 + 0.3 * confidence) : 0;
      scored.push({ product: p, score: Math.round(score * 10) / 10, parts: parts, overBudget: parts.budget === 0, complete: !!(p.specs && (p.specs.width_fit || p.specs.weight_oz)) });
    });

    scored.sort(function (x, y) {
      if (y.score !== x.score) return y.score - x.score;
      if (x.complete !== y.complete) return x.complete ? -1 : 1;
      if (x.product.price !== y.product.price) return x.product.price - y.product.price;
      return String(x.product.title).localeCompare(String(y.product.title));
    });

    // One per model (colorways and editions share a model name or spec source).
    function modelKey(p) {
      if (p.specs && p.specs.spec_source) return "src:" + p.specs.spec_source;
      return "t:" + lower(p.title).replace(/\s+-\s+.*$|\(.*\)|\b(black|white|blue|red|gum|green|lime|gray|grey|navy|pink)\b/g, "").replace(/[^a-z0-9]+/g, " ").trim();
    }
    var picks = [], seen = {};
    function take(list, allowOver) {
      for (var i = 0; i < list.length && picks.length < max; i++) {
        var s = list[i];
        if (s.overBudget && (!allowOver || s.product.price > cap * 1.15)) continue;
        var key = modelKey(s.product);
        if (seen[key]) continue;
        seen[key] = true;
        picks.push(s);
      }
    }
    take(scored.filter(function (s) { return !s.overBudget; }), false);
    if (picks.length < max) take(scored.filter(function (s) { return s.overBudget; }), true);

    var LABELS = ["Best Match", "Strong Match", "Alternative"];
    picks.forEach(function (s, i) {
      s.label = LABELS[i] || "Alternative";
      s.best = i === 0;
      s.variant = size !== null ? variantForSize(s.product, size, answers.width === "wide") : null;
      s.explanation = explain(s, answers, sport);
      s.sizingTip = sizingTip(s.product);
      s.sizingTipKey = sizingTipKey(s.product);
    });
    return { picks: picks, scored: scored, rejected: rejected, poolSize: pool.length, profile: profile(answers) };
  }

  // "Why we picked it" is built from reason keys, so each app can show it in its
  // own language (assets/paddle-quiz-i18n.js has the kiosk's translations).
  var REASONS_EN = {
    in_size: "In stock in size {size}.",
    wide_version: "Also comes in a wide (2E) width.",
    width_fit: "It has {width}, a good match for {feet} feet.",
    arch: "Arch support: {arch}.",
    light: "One of the lighter options at {oz} oz{sizeNote}.",
    light_size: " (size {size})",
    guarantee: "Has a 6-month outsole durability guarantee (ask us for the terms).",
    no_breakin: "Comfortable out of the box, with no break-in needed.",
    slight_breakin: "Only a short break-in.",
    value: "A good price for what you get.",
    frequent: "Built for frequent play, with an outsole durability guarantee.",
    pickleball: "Made for pickleball.",
    fit_wa: "Fit: {width}, {arch} arch support. Try it on to be sure.",
    fit_w: "Fit: {width}. Try it on to be sure.",
    fit_a: "Fit: {arch} arch support. Try it on to be sure.",
    missing: "Some fit details aren't on file, so try it on or ask a team member.",
    over_budget: "It's a little above your budget.",
    tip_long: "Runs a little long. Consider a half size down.",
    tip_short: "Runs a little short. If you're between sizes, go up a half size.",
    w_snug: "a snug fit", w_medium: "a medium (standard) width", w_roomy: "a roomier-than-average fit", w_wide: "a wide fit",
    feet_narrow: "narrow", feet_regular: "regular", feet_wide: "wide"
  };

  // Reason keys for a pick, most important first.
  function reasons(s, a, sport) {
    var p = s.product, parts = s.parts, out = [];
    // Only mention what actually fits well (part score 0.75 or more).
    function add(key, k, v) { if (parts[key] !== null && parts[key] !== undefined && parts[key] >= 0.75) out.push({ gain: parts[key] * WEIGHTS[key], k: k, v: v || {} }); }
    var cls = widthClass(p);
    if (a.width && a.width !== "unsure") {
      if (a.width === "wide" && hasWideVersion(p)) add("width", "wide_version");
      else if (cls) add("width", "width_fit", { width: "w_" + cls, feet: "feet_" + a.width });
    }
    var arch = p.specs && p.specs.arch;
    if (arch && a.arch && a.arch !== "unsure") add("arch", "arch", { arch: arch });
    var w = weightOz(p);
    if (a.priority === "light" && w !== null) add("priority", "light", { oz: w, size: p.specs.weight_size || "" });
    if (a.priority === "durable" && guarantee(p)) add("priority", "guarantee");
    if (a.priority === "comfort" && breakIn(p) === "none") add("priority", "no_breakin");
    if (a.priority === "comfort" && breakIn(p) === "slight") add("priority", "slight_breakin");
    if (a.priority === "value") add("priority", "value");
    if (a.often === "4" && a.priority !== "durable" && guarantee(p)) add("often", "frequent");
    if (sport === "pickleball" && isPickleballShoe(p)) add("sport", "pickleball");
    out.sort(function (x, y) { return y.gain - x.gain; });
    var list = out.slice(0, 3).map(function (x) { return { k: x.k, v: x.v }; });
    if (!list.length && genderOf(p) !== "kids") {
      if (cls && arch) list.push({ k: "fit_wa", v: { width: "w_" + cls, arch: arch } });
      else if (cls) list.push({ k: "fit_w", v: { width: "w_" + cls } });
      else if (arch) list.push({ k: "fit_a", v: { arch: arch } });
    }
    if (a.size && a.size !== "any") list.unshift({ k: "in_size", v: { size: a.size } });
    if (!s.complete && genderOf(p) !== "kids") list.push({ k: "missing", v: {} });
    if (s.overBudget) list.push({ k: "over_budget", v: {} });
    return list;
  }

  // Text for reason keys. `T` holds the templates (REASONS_EN or a translation);
  // `value` translates fit values such as "Low" (English: lower case as is).
  function renderReasons(list, T, value) {
    T = T || REASONS_EN;
    value = value || function (x) { return String(x).toLowerCase(); };
    return list.map(function (r) {
      var v = r.v || {};
      var t = T[r.k] || REASONS_EN[r.k] || "";
      var fill = {
        size: v.size, oz: v.oz,
        sizeNote: v.size ? (T.light_size || REASONS_EN.light_size).split("{size}").join(v.size) : "",
        width: v.width ? (T[v.width] || REASONS_EN[v.width]) : "",
        feet: v.feet ? (T[v.feet] || REASONS_EN[v.feet]) : "",
        arch: v.arch ? value(v.arch) : ""
      };
      Object.keys(fill).forEach(function (k) { t = t.split("{" + k + "}").join(fill[k] === undefined ? "" : String(fill[k])); });
      return t;
    }).join(T.sep !== undefined ? T.sep : " ");
  }

  function explain(s, a, sport) {
    s.reasons = reasons(s, a, sport);
    return renderReasons(s.reasons, REASONS_EN);
  }

  // Rows for "What you're looking for". `q` and `id` let an app translate them.
  function profile(a) {
    var rows = [{ q: "who", id: a.who, k: "For", v: (optionById("who", a.who) || {}).text || "" }];
    if (a.size) rows.push({ q: "size", id: a.size, k: "Size", v: a.size === "any" ? "Any" : String(a.size) });
    if (a.width) rows.push({ q: "width", id: a.width, k: "Width", v: (optionById("width", a.width) || {}).text || "" });
    if (a.arch) rows.push({ q: "arch", id: a.arch, k: "Arch", v: (optionById("arch", a.arch) || {}).text || "" });
    if (a.priority) rows.push({ q: "priority", id: a.priority, k: "Priority", v: (optionById("priority", a.priority) || {}).text || "" });
    return rows;
  }

  // Fit facts for cards and detail screens. Values are the store's data, shown as is.
  function facts(p) {
    var w = weightOz(p);
    var cls = widthClass(p);
    return [
      { key: "width", label: "Width", value: hasWideVersion(p) ? "Standard and wide (2E)" : (p.specs && p.specs.width_fit) || (cls === "wide" ? "Wide" : null) },
      { key: "length", label: "Length", value: (p.specs && p.specs.length_fit) || null },
      { key: "arch", label: "Arch", value: (p.specs && p.specs.arch) || null },
      { key: "weight", label: "Weight", value: w !== null ? w + " oz" + (p.specs.weight_size ? " (size " + p.specs.weight_size + ")" : "") : null, oz: w, size: (p.specs && p.specs.weight_size) || "" },
      { key: "breakin", label: "Break-in", value: (p.specs && p.specs.break_in) || null },
      { key: "guarantee", label: "Durability guarantee", value: guarantee(p) === null ? null : (guarantee(p) ? "Yes (6-month outsole)" : "No") }
    ];
  }

  return {
    QUESTIONS: QUESTIONS, WEIGHTS: WEIGHTS, questionsFor: questionsFor, optionById: optionById,
    eligible: eligible, genderOf: genderOf, isPadel: isPadel, isPickleballShoe: isPickleballShoe,
    availableSizes: availableSizes, sizesFor: sizesFor, variantForSize: variantForSize,
    widthClass: widthClass, hasWideVersion: hasWideVersion, archLevel: archLevel, weightOz: weightOz,
    guarantee: guarantee, breakIn: breakIn, sizingTip: sizingTip, sizingTipKey: sizingTipKey, guard: guard, recommend: recommend, facts: facts,
    REASONS_EN: REASONS_EN, renderReasons: renderReasons
  };
});
